const COLOR_PATTERN = /^#[\dA-F]{6}$/;
const IMAGE_URL_PATTERN = /^https?:\/\/\S+$/;
export const APP_UI_IMAGE_URL_MAX_LENGTH = 1000;
const CONTROL_CHARACTERS = /[\u0000-\u001F\u007F]/;
const channelLuminance = (channel) => {
    const c = channel / 255;
    return c <= 0.039_28 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};
export function relativeLuminance(hex) {
    const value = Number.parseInt(hex.slice(1), 16);
    return (0.2126 * channelLuminance((value >> 16) & 255) +
        0.7152 * channelLuminance((value >> 8) & 255) +
        0.0722 * channelLuminance(value & 255));
}
export function contrastRatio(a, b) {
    const [lighter, darker] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x);
    return (lighter + 0.05) / (darker + 0.05);
}
export function parseSizeTokenReference(value) {
    if (typeof value !== 'string')
        return null;
    const match = /^\$(spacing|radius|fontSize|dimension)\.([\da-z][\da-z-]{0,39})$/.exec(value);
    return match ? { group: match[1] ?? '', name: match[2] ?? '' } : null;
}
export function isValidAppUiSettingValue(definition, value) {
    switch (definition.type) {
        case 'size': {
            if (typeof value === 'number')
                return Number.isFinite(value) && value >= definition.min && value <= definition.max;
            return parseSizeTokenReference(value)?.group === definition.tokenGroup;
        }
        case 'boolean':
            return typeof value === 'boolean';
        case 'integer':
            return (typeof value === 'number' &&
                Number.isInteger(value) &&
                value >= definition.min &&
                value <= definition.max);
        case 'enum':
            return typeof value === 'string' && definition.options.some((o) => o.value === value);
        case 'color':
            return (typeof value === 'string' &&
                COLOR_PATTERN.test(value) &&
                (definition.contrast ?? []).every((rule) => contrastRatio(value, rule.with) >= rule.min));
        case 'text':
            return (typeof value === 'string' &&
                value.length <= definition.maxLength &&
                value === value.trim() &&
                !CONTROL_CHARACTERS.test(value));
        case 'image':
            return (typeof value === 'string' &&
                (value === '' ||
                    (value.length <= APP_UI_IMAGE_URL_MAX_LENGTH && IMAGE_URL_PATTERN.test(value))));
        case 'order': {
            if (!Array.isArray(value) || value.length !== definition.items.length)
                return false;
            const seen = new Set();
            for (const item of value) {
                if (typeof item !== 'string' || seen.has(item))
                    return false;
                if (!definition.items.some((i) => i.value === item))
                    return false;
                seen.add(item);
            }
            return true;
        }
    }
}
export function appUiSettingValuesEqual(a, b) {
    if (Array.isArray(a) || Array.isArray(b)) {
        return (Array.isArray(a) &&
            Array.isArray(b) &&
            a.length === b.length &&
            a.every((item, index) => item === b[index]));
    }
    return a === b;
}
//# sourceMappingURL=validate.js.map